import React, { useState, useEffect, useRef } from "react";
import {
  FiLink,
  FiXCircle,
  FiDollarSign,
  FiLock,
  FiGlobe,
  FiRefreshCw,
  FiStar,
  FiTrash2,
  FiX,
  FiKey,
  FiMail,
  FiServer,
  FiShield,
  FiExternalLink,
} from "react-icons/fi";
import { useApi } from "../../hooks/useApi";
import { useAuthStore } from "../../stores";
import AccountDetails from "./AccountDetails";
import { Loader } from "../Common/Loader";
import { socketService } from "../../services/socketService";
import { showToast } from "../../utils/toast";
import PrimaryBrokerChangeModal from "./PrimaryBrokerChangeModal";
import { BrokerageConnection } from "../../types";
import { useQueryClient } from "@tanstack/react-query";
import { brokerageApi } from "../../api";
import { isSubscriptionAccessAllowed } from "../../utils/subscriptionAccess";

interface ConnectBrokerageProps {
  user: any;
  refresh: () => void;
  selectedBrokerId?: string | null;
  onBrokerChange?: (brokerId: string | null) => void;
}

const ConnectBrokerage: React.FC<ConnectBrokerageProps> = ({
  user,
  refresh,
  selectedBrokerId,
  onBrokerChange,
}) => {
  const { setUser } = useAuthStore();
  const [showAccountDetails, setShowAccountDetails] = useState<boolean>(false);
  const [connections, setConnections] = useState<BrokerageConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [syncingConnection, setSyncingConnection] = useState<string | null>(
    null,
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [primaryConnectionData, setPrimaryConnectionData] = useState<any>(null);
  // Start with loading=true to avoid flash of "no data" before first fetch
  const [loadingPrimaryData, setLoadingPrimaryData] = useState(true);
  const [showPrimaryChangeModal, setShowPrimaryChangeModal] = useState(false);
  const [pendingPrimaryChange, setPendingPrimaryChange] = useState<{
    connectionId: string;
    connectionName: string;
    activeRelationships: any[];
  } | null>(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  /** When set, shows confirm modal before removing a brokerage connection */
  const [disconnectPending, setDisconnectPending] = useState<{
    connectionId: string;
    brokerName: string;
  } | null>(null);

  // OANDA connection form state
  const [showOandaModal, setShowOandaModal] = useState<boolean>(false);
  const [oandaAccessToken, setOandaAccessToken] = useState<string>("");

  // GenesisFX connection
  const [showGenesisFxModal, setShowGenesisFxModal] = useState<boolean>(false);
  const [genesisFxEmail, setGenesisFxEmail] = useState<string>("");
  const [genesisFxPassword, setGenesisFxPassword] = useState<string>("");
  const [genesisFxStep, setGenesisFxStep] = useState<"credentials" | "select-account">("credentials");
  const [genesisFxAccounts, setGenesisFxAccounts] = useState<
    Array<{
      id: string;
      accountNumber: string;
      displayName?: string;
      name?: string;
      currency: string;
      balance: number | null;
    }>
  >([]);
  const [genesisFxConnectionId, setGenesisFxConnectionId] = useState<string | null>(null);
  const [selectedGenesisAccountId, setSelectedGenesisAccountId] = useState<string | null>(null);
  const GENESIS_FX_SERVER = "GenFX";

  // Webull / SnapTrade account picker (after OAuth — Genesis-style select)
  const [showWebullSelectModal, setShowWebullSelectModal] = useState(false);
  const [webullAccounts, setWebullAccounts] = useState<
    Array<{
      id: string;
      accountNumber: string;
      displayName?: string;
      name?: string;
      brokerageName?: string;
      currency: string;
      balance: number | null;
      accountType?: string;
      alreadyConnected?: boolean;
    }>
  >([]);
  const [selectedWebullAccountId, setSelectedWebullAccountId] = useState<string | null>(null);
  const [loadingWebullAccounts, setLoadingWebullAccounts] = useState(false);
  const webullPickerOpeningRef = useRef(false);

  // Alpaca connection (direct API keys — not SnapTrade)
  const [showAlpacaModal, setShowAlpacaModal] = useState<boolean>(false);
  const [alpacaApiKey, setAlpacaApiKey] = useState<string>("");
  const [alpacaApiSecret, setAlpacaApiSecret] = useState<string>("");
  const [alpacaIsPaper, setAlpacaIsPaper] = useState<boolean>(true);

  const resetAlpacaModal = () => {
    setShowAlpacaModal(false);
    setAlpacaApiKey("");
    setAlpacaApiSecret("");
    setAlpacaIsPaper(true);
  };

  const resetGenesisFxModal = () => {
    setShowGenesisFxModal(false);
    setGenesisFxEmail("");
    setGenesisFxPassword("");
    setGenesisFxStep("credentials");
    setGenesisFxAccounts([]);
    setGenesisFxConnectionId(null);
    setSelectedGenesisAccountId(null);
  };

  const resetWebullSelectModal = () => {
    setShowWebullSelectModal(false);
    setWebullAccounts([]);
    setSelectedWebullAccountId(null);
    setLoadingWebullAccounts(false);
  };

  const queryClient = useQueryClient();

  const { refetch: refetchUser } = useApi.useCurrentUser();
  const { data: subscriptionData } = useApi.useSubscriptionStatus();

  const platformPlan = String(
    (subscriptionData?.subscription as any)?.plan || user?.plan || ''
  ).toLowerCase();

  // Masters don't need subscriptions - only followers do
  const isMaster = user?.role === "master" || user?.role === "master_pending";

  const classifyConnectionKind = (conn: BrokerageConnection | null | undefined) => {
    const type = String(conn?.connectionType || '').toLowerCase();
    const name = String(conn?.brokerageName || '').toLowerCase();
    const slug = String((conn as any)?.brokerageSlug || '').toLowerCase();
    const haystack = `${name} ${slug}`;
    if (type === 'genesis') return 'genesis';
    if (haystack.includes('webull') || type === 'snaptrade') return 'webull';
    return type || 'other';
  };

  const webullConnectionCount = connections.filter(
    (c) => classifyConnectionKind(c) === 'webull',
  ).length;
  const genesisConnectionCount = connections.filter(
    (c) => classifyConnectionKind(c) === 'genesis',
  ).length;

  const isLegacyFivePlan =
    platformPlan === 'og' ||
    platformPlan === 'old_user' ||
    platformPlan === 'special_users' ||
    platformPlan === 'if&brice_user' ||
    platformPlan === 'other_pro' ||
    platformPlan === 'full_access' ||
    platformPlan === 'full_access_paid';

  // Growth (Forex): 1 Genesis | General: 1+1 | Pro / og / etc: 5
  const maxBrokerAccounts =
    isMaster || platformPlan === 'pro' || isLegacyFivePlan
      ? 5
      : platformPlan === 'general'
        ? 2
        : 1;

  const atBrokerLimit = connections.length >= maxBrokerAccounts;
  const createCheckoutMutation = useApi.useCreateCheckoutSession({
    onError: (error) => {
      console.error("Error creating checkout session:", error);
    },
  });

  // OANDA connection mutation
  const saveOandaTokenMutation = useApi.useSaveOandaToken({
    onSuccess: async (data) => {
      showToast(
        "success",
        data.message || "OANDA access token saved successfully!",
      );
      setShowOandaModal(false);
      setOandaAccessToken("");
      await loadConnections();

      // Auto-sync OANDA account(s) so balance and data show without user clicking Sync
      const connectionIds =
        (data as { connections?: { id: string }[] }).connections?.map(
          (c) => c.id,
        ) || [];
      if (connectionIds.length > 0) {
        showToast("info", "Syncing account data...");
        for (const connectionId of connectionIds) {
          try {
            await brokerageApi.syncConnection(connectionId);
          } catch (syncErr: any) {
            console.warn(
              "[OANDA] Auto-sync failed for connection",
              connectionId,
              syncErr?.message,
            );
          }
        }
        await loadConnections();
      }

      // Refresh user data
      const userResponse = await refetchUser();
      if (userResponse.data?.user) {
        setUser(userResponse.data.user);
      }
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      refresh();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      const errorMessage =
        errorData?.error ||
        error.message ||
        "Failed to save OANDA access token";
      showToast("error", errorMessage);
    },
  });

  const connectAlpacaMutation = useApi.useConnectAlpaca({
    onSuccess: async (data) => {
      showToast(
        "success",
        data.message || "Alpaca account connected successfully!",
      );
      resetAlpacaModal();
      await loadConnections();

      const connectionId = data.connection?._id;
      if (connectionId) {
        showToast("info", "Syncing account data...");
        try {
          await brokerageApi.syncConnection(connectionId);
        } catch (syncErr: any) {
          console.warn(
            "[Alpaca] Auto-sync failed for connection",
            connectionId,
            syncErr?.message,
          );
        }
        await loadConnections();
      }

      const userResponse = await refetchUser();
      if (userResponse.data?.user) {
        setUser(userResponse.data.user);
      }
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      refresh();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error.message ||
        "Failed to connect Alpaca account";
      if (errorData?.details) {
        showToast("error", `${errorMessage}\n\n${errorData.details}`);
      } else {
        showToast("error", errorMessage);
      }
    },
  });

  // Growth (Forex): 1 Genesis only | General: 1 Webull + 1 Genesis | Pro / og / etc: up to 5 any
  const canConnectWebull =
    !atBrokerLimit &&
    (isMaster ||
      platformPlan === 'pro' ||
      isLegacyFivePlan ||
      (platformPlan === 'general' && webullConnectionCount < 1));
  const canConnectGenesis =
    !atBrokerLimit &&
    (isMaster ||
      platformPlan === 'pro' ||
      isLegacyFivePlan ||
      (platformPlan === 'growth' && genesisConnectionCount < 1) ||
      (platformPlan === 'general' && genesisConnectionCount < 1));
  const brokerPlanHint =
    platformPlan === "growth"
      ? "Growth (Forex): connect 1 GenesisFX only."
      : platformPlan === "general"
        ? "General: connect 1 Webull and 1 GenesisFX."
        : platformPlan === "pro"
          ? "Premium: connect up to 5 accounts (Webull or GenesisFX)."
          : isLegacyFivePlan
            ? "Your plan: connect up to 5 accounts (Webull or GenesisFX)."
            : null;
  // TEMP: allow brokerage connect without active subscription payment
  const hasActiveSubscription = true;
  // const hasActiveSubscription = isMaster
  //   ? true
  //   : isSubscriptionAccessAllowed(subscriptionData);

  const hasBrokerageConnected = !!(
    user?.hasBrokerageConnected || connections.length > 0
  );

  // Find primary connection
  const primaryConnection =
    connections.find((c) => c.isPrimary) || connections[0] || null;

  // Determine which connection to display: use selectedBrokerId if available, otherwise use primary
  const displayConnection = selectedBrokerId
    ? connections.find(
      (c) =>
        c._id?.toString() === selectedBrokerId?.toString() ||
        c._id === selectedBrokerId,
    ) || primaryConnection
    : primaryConnection;

  const displayConnectionId = displayConnection?._id?.toString();

  // Note: Broker connection management is handled via SnapTrade, Alpaca, GenesisFX, and OANDA flows.

  // Load connections
  const loadConnections = async () => {
    try {
      setLoadingConnections(true);
      const result = await brokerageApi.getConnections();
      const loadedConnections = result.connections || [];
      setConnections(loadedConnections);
    } catch (error) {
      console.error("Failed to load connections:", error);
    } finally {
      setLoadingConnections(false);
    }
  };

  useEffect(() => {
    loadConnections();

    const tryOpenWebullPicker = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const snaptradeCallback = urlParams.get("snaptrade_callback");
      const hasCallbackInUrl =
        snaptradeCallback === "true" || snaptradeCallback === "1";
      let pendingSelect = false;
      try {
        pendingSelect =
          sessionStorage.getItem("webull_pending_account_select") === "1";
      } catch {
        pendingSelect = false;
      }

      if (!hasCallbackInUrl && !pendingSelect) return;

      if (hasCallbackInUrl) {
        window.history.replaceState({}, "", window.location.pathname);
      }
      try {
        sessionStorage.removeItem("webull_pending_account_select");
      } catch {
        /* ignore */
      }
      openWebullAccountPicker();
    };

    tryOpenWebullPicker();
    window.addEventListener("webull:select-accounts", tryOpenWebullPicker);
    return () => {
      window.removeEventListener("webull:select-accounts", tryOpenWebullPicker);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWebullAccountPicker = async () => {
    if (webullPickerOpeningRef.current) return;
    webullPickerOpeningRef.current = true;
    try {
      setIsConnecting(true);
      setLoadingWebullAccounts(true);
      setShowWebullSelectModal(true);
      showToast("info", "Fetching your Webull accounts...");

      const result = await brokerageApi.listSnaptradeAccounts();

      if (!result.success) {
        showToast(
          "error",
          result.error || result.message || "Failed to load Webull accounts",
        );
        resetWebullSelectModal();
        return;
      }

      const accounts = result.accounts || [];
      setWebullAccounts(accounts);

      const firstAvailable =
        accounts.find((a) => !a.alreadyConnected)?.id || accounts[0]?.id || null;
      setSelectedWebullAccountId(firstAvailable);

      if (accounts.length === 0) {
        showToast(
          "info",
          result.message ||
            "No accounts found yet. Wait a moment and try Connect Webull again.",
        );
      } else {
        showToast(
          "info",
          result.message || "Select the Webull account you want to connect.",
        );
      }
    } catch (error: any) {
      console.error("Webull account list error:", error);
      showToast(
        "error",
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load Webull accounts. Please try again.",
      );
      resetWebullSelectModal();
    } finally {
      setLoadingWebullAccounts(false);
      setIsConnecting(false);
      webullPickerOpeningRef.current = false;
    }
  };

  const handleSelectWebullAccount = async () => {
    if (!selectedWebullAccountId) {
      showToast("error", "Please select an account");
      return;
    }

    const selected = webullAccounts.find((a) => a.id === selectedWebullAccountId);
    if (selected?.alreadyConnected) {
      showToast("info", "That account is already connected.");
      resetWebullSelectModal();
      return;
    }

    try {
      setIsConnecting(true);
      showToast("info", "Connecting selected Webull account...");

      const result = await brokerageApi.selectSnaptradeAccount(selectedWebullAccountId);

      if (result.success) {
        showToast(
          "success",
          result.message || "Webull account connected successfully!",
        );
        resetWebullSelectModal();
        await loadConnections();
        const userResponse = await refetchUser();
        if (userResponse.data?.user) {
          setUser(userResponse.data.user);
        }
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["connections"] });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        refresh();
      } else {
        showToast("error", result.message || "Failed to connect account");
      }
    } catch (error: any) {
      console.error("Webull account select error:", error);
      showToast(
        "error",
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to connect selected account",
      );
    } finally {
      setIsConnecting(false);
    }
  };

  // Auto-show account details when connected
  useEffect(() => {
    if (hasBrokerageConnected && !showAccountDetails) {
      setShowAccountDetails(true);
    }
  }, [hasBrokerageConnected, showAccountDetails]);

  // Clear and refetch data when selectedBrokerId changes (to avoid showing stale data)
  // IMPORTANT: Do NOT include connections in dependency array to prevent infinite loops
  // Instead, only react to selectedBrokerId changes
  useEffect(() => {
    if (selectedBrokerId !== undefined) {
      // Clear old connection data immediately when broker selection changes
      // Set loading to true to show loader instead of "no data" message
      setLoadingPrimaryData(true);
      setPrimaryConnectionData(null);
    }
  }, [selectedBrokerId]); // Removed connections from dependency array to prevent infinite loops

  // Fetch account details for display connection (for SnapTrade connections)
  // Optimize: Only fetch positions when showAccountDetails is true and connection ID changes
  // Use cached connection values (accountBalance, buyingPower) from the connection object
  // Don't refetch on every balance change - only when connection changes or user explicitly refreshes
  useEffect(() => {
    const fetchDisplayConnectionData = async () => {
      // Clear old data immediately when broker changes to prevent showing stale data
      if (!displayConnection || !showAccountDetails) {
        setPrimaryConnectionData(null);
        return;
      }

      // Use connection object's cached values
      // (the queries only fetch from primary account, so we need connection-specific data)

      try {
        setLoadingPrimaryData(true);

        // Always fetch live account values from broker API for selected connection.
        const liveAccountRes = await brokerageApi.getAccount(
          displayConnection._id,
        );
        const liveAccount = liveAccountRes?.account || {};

        // Only fetch positions (this is the slow call)
        let positions: any[] = [];
        try {
          const positionsRes = await brokerageApi.getPositions(
            displayConnection._id,
          );
          if (positionsRes.success) {
            positions = positionsRes.positions || [];
          }
        } catch (posError: any) {
          console.error(
            "❌ [Frontend] Failed to fetch positions:",
            posError.message,
          );
          // Continue with empty positions array
        }

        // Create account object compatible with AccountDetails component
        const accountData = {
          account: {
            id:
              liveAccount.id ||
              displayConnection.accountId ||
              displayConnection._id.toString(),
            portfolio_value:
              liveAccount.portfolio_value ?? liveAccount.equity ?? 0,
            cash: liveAccount.cash ?? 0,
            equity: liveAccount.equity ?? liveAccount.portfolio_value ?? 0,
            buying_power:
              liveAccount.buying_power ?? liveAccount.buyingPower ?? 0,
            currency:
              liveAccount.currency || displayConnection.currency || "USD",
            accountType:
              liveAccount.accountType ||
              displayConnection.accountType ||
              "live",
          },
          positions: positions,
          brokerageName: displayConnection.brokerageName,
        };
        setPrimaryConnectionData(accountData);
      } catch (error) {
        console.error("Failed to fetch display connection data:", error);
        if (displayConnection.connectionType === "genesis") {
          let positions: any[] = [];
          try {
            const positionsRes = await brokerageApi.getPositions(displayConnection._id);
            if (positionsRes.success) positions = positionsRes.positions || [];
          } catch (_) { }
          setPrimaryConnectionData({
            account: {
              id: displayConnection.accountId || displayConnection._id.toString(),
              portfolio_value: displayConnection.accountBalance || 0,
              cash: displayConnection.accountBalance || 0,
              equity: displayConnection.accountBalance || 0,
              buying_power: displayConnection.buyingPower || displayConnection.accountBalance || 0,
              currency: displayConnection.currency || "USD",
              accountType: displayConnection.accountType || "live",
            },
            positions,
            brokerageName: displayConnection.brokerageName,
          });
        } else {
          setPrimaryConnectionData(null);
        }
      } finally {
        setLoadingPrimaryData(false);
      }
    };

    fetchDisplayConnectionData();
    // IMPORTANT: Do NOT include connections in dependency array to prevent infinite loops
    // The displayConnection object already contains the necessary data, and displayConnectionId changes when needed
  }, [
    displayConnectionId,
    selectedBrokerId,
    showAccountDetails,
    displayConnection?._id,
  ]);

  // Listen for subscription updates via WebSocket
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleSubscriptionUpdate = (data: any) => {
      console.log("💳 Subscription update received:", data);
      refetchUser();
    };

    socket.on("subscription:update", handleSubscriptionUpdate);

    return () => {
      socket.off("subscription:update", handleSubscriptionUpdate);
    };
  }, [refetchUser]);

  const handleSubscribe = () => {
    createCheckoutMutation.mutate('general');
  };

  // Handle Connect  Portal - redirects to full broker selection
  const handleConnectSnapTrade = async () => {
    if (!canConnectWebull) {
      showToast(
        "error",
        atBrokerLimit
          ? `Maximum of ${maxBrokerAccounts} broker account${maxBrokerAccounts === 1 ? "" : "s"} allowed.`
          : platformPlan === "growth"
            ? "Growth (Forex) can only connect GenesisFX, not Webull. Upgrade to General or Premium."
            : platformPlan === "general" && webullConnectionCount >= 1
              ? "General plan allows only 1 Webull account (and 1 GenesisFX)."
              : "Your plan cannot connect Webull. Upgrade to General or Premium.",
      );
      return;
    }
    if (atBrokerLimit) {
      showToast(
        "error",
        `Maximum of ${maxBrokerAccounts} broker accounts allowed. Please disconnect an account before adding a new one.`,
      );
      return;
    }

    // Check subscription for followers
    if (!isMaster && !hasActiveSubscription) {
      showToast("error", "Please subscribe to connect a brokerage account");
      return;
    }

    try {
      setIsConnecting(true);
      showToast("info", "Opening broker connection portal...");

      const result: any = await brokerageApi.getSnaptradePortal();

      if (result.success && result.redirectURI) {
        // Redirect to SnapTrade portal
        window.location.href = result.redirectURI;
      } else {
        showToast("error", "Failed to open connection portal");
      }
    } catch (error: any) {
      console.error("Connect broker error:", error);

      // Check for specific error types
      const errorStatus = error.response?.status;
      const errorData = error.response?.data;

      let errorMessage = "Failed to connect broker";

      if (errorStatus === 401) {
        errorMessage =
          errorData?.error ||
          errorData?.message ||
          "SnapTrade authentication failed. Please contact support to verify API credentials.";
        console.error(
          "🔐 SnapTrade authentication error - credentials may be invalid",
        );
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast("error", errorMessage);
      setIsConnecting(false);
    }
  };

  const handleSetPrimary = async (connectionId: string) => {
    setSettingPrimaryId(connectionId);
    try {
      const result = await brokerageApi.setPrimaryConnection(connectionId);
      if (result.success) {
        // If there are active relationships, show confirmation modal
        if (
          result.hasActiveRelationships &&
          result.activeRelationships &&
          result.activeRelationships.length > 0
        ) {
          const connection = connections.find((c) => c._id === connectionId);
          setPendingPrimaryChange({
            connectionId,
            connectionName: connection?.brokerageName || "Selected Broker",
            activeRelationships: result.activeRelationships,
          });
          setShowPrimaryChangeModal(true);
        } else {
          // No active relationships, proceed normally
          showToast("success", result.message || "Primary connection updated");
          // Reload connections first to ensure we have updated primary status
          await loadConnections();
          queryClient.invalidateQueries({ queryKey: ["dashboard"] });
          setPrimaryConnectionData(null);
          // Wait a bit for state to update, then update selected broker to match the new primary broker
          setTimeout(() => {
            if (onBrokerChange) {
              onBrokerChange(connectionId);
            }
          }, 100);
        }
      }
    } catch (error: any) {
      showToast("error", error.message || "Failed to set primary");
    } finally {
      setSettingPrimaryId(null);
    }
  };

  const handleConfirmPrimaryChange = async (
    selectedRelationshipIds: string[],
  ) => {
    if (!pendingPrimaryChange) return;

    try {
      // Update relationships to use the new primary broker
      // If selectedRelationshipIds is empty (user clicked "Skip"), relationships stay with old broker
      const result = await brokerageApi.updateRelationshipsForPrimary(
        pendingPrimaryChange.connectionId,
        selectedRelationshipIds,
      );

      if (result.success) {
        if (selectedRelationshipIds.length === 0) {
          showToast(
            "success",
            "Primary connection updated. You can follow masters later.",
          );
        } else {
          showToast(
            "success",
            result.message ||
            `Updated ${selectedRelationshipIds.length} relationship(s)`,
          );
        }
      } else {
        showToast("error", result.message || "Failed to update relationships");
      }

      await loadConnections();
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard", "masters"] });
      setPrimaryConnectionData(null);
      setShowPrimaryChangeModal(false);
      // Wait a bit for state to update, then update selected broker to match the new primary broker
      setTimeout(() => {
        if (onBrokerChange) {
          onBrokerChange(pendingPrimaryChange.connectionId);
        }
      }, 100);
      setPendingPrimaryChange(null);
    } catch (error: any) {
      showToast("error", error.message || "Failed to update relationships");
    }
  };

  const handleSync = async (connectionId: string) => {
    try {
      setSyncingConnection(connectionId);
      const result = await brokerageApi.syncConnection(connectionId);
      if (result.success) {
        showToast("success", "Account synced successfully");
        await loadConnections();
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      }
    } catch (error: any) {
      showToast("error", error.message || "Failed to sync");
    } finally {
      setSyncingConnection(null);
    }
  };

  const performDisconnect = async (connectionId: string): Promise<boolean> => {
    setDisconnectingId(connectionId);
    try {
      const result = await brokerageApi.disconnectConnection(connectionId);
      if (result.success) {
        showToast("success", result.message || "Brokerage disconnected");

        const wasSelectedBroker = selectedBrokerId === connectionId;

        await loadConnections();

        if (wasSelectedBroker && onBrokerChange) {
          setTimeout(async () => {
            const updatedConnections = await brokerageApi.getConnections();
            const nextPrimary = updatedConnections.connections?.find(
              (c) => c.isPrimary,
            );
            const firstConnection = updatedConnections.connections?.[0];
            const newSelectedBroker = nextPrimary || firstConnection;

            if (newSelectedBroker) {
              onBrokerChange(newSelectedBroker._id);
            } else {
              onBrokerChange(null);
            }
          }, 100);
        }

        const userResponse = await refetchUser();
        if (userResponse.data?.user) {
          setUser(userResponse.data.user);
        }
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["copyRelationships"] });
        queryClient.invalidateQueries({ queryKey: ["copyTrading"] });
        queryClient.invalidateQueries({ queryKey: ["dashboard", "masters"] });
        refresh();
        return true;
      }
      return false;
    } catch (error: any) {
      showToast("error", error.message || "Failed to disconnect");
      return false;
    } finally {
      setDisconnectingId(null);
    }
  };

  const openDisconnectConfirm = (connection: BrokerageConnection) => {
    setDisconnectPending({
      connectionId: connection._id,
      brokerName: connection.brokerageName?.trim() || "this brokerage account",
    });
  };

  // Note: handleRefreshAccount removed - account refresh is now handled via
  // the AccountDetails component's onRefresh prop which reloads connections

  // Manual sync from SnapTrade
  const handleManualSync = async () => {
    try {
      setIsConnecting(true);
      showToast("info", "Syncing accounts from SnapTrade...");

      const result = await brokerageApi.syncSnaptradeAccounts();
      if (result.success) {
        await loadConnections();
        const userResponse = await refetchUser();
        if (userResponse.data?.user) {
          setUser(userResponse.data.user);
        }
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        refresh();

        if (result.count > 0) {
          showToast(
            "success",
            result.message || `Synced ${result.count} account(s) successfully!`,
          );
        } else if (result.skippedDisconnectedBrokers?.length) {
          showToast("info", result.message);
        } else {
          showToast(
            "info",
            result.message ||
            'No accounts found. Click "Connect Webull" to connect a brokerage.',
          );
        }
      }
    } catch (error: any) {
      console.error("Manual sync error:", error);
      showToast(
        "error",
        error.response?.data?.error ||
        "Failed to sync. Make sure you have connected a broker on SnapTrade.",
      );
    } finally {
      setIsConnecting(false);
    }
  };
  const finalizeGenesisConnection = async (connectionId: string) => {
    showToast("info", "Syncing account data...");
    try {
      await brokerageApi.syncConnection(connectionId);
    } catch (syncErr: any) {
      console.warn(
        "[GenesisFX] Auto-sync failed for connection",
        connectionId,
        syncErr?.message,
      );
    }

    await loadConnections();
    const userResponse = await refetchUser();
    if (userResponse.data?.user) {
      setUser(userResponse.data.user);
    }
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    refresh();
  };

  const handleConnectGenesis = async () => {
    if (!canConnectGenesis) {
      showToast(
        "error",
        atBrokerLimit
          ? `Maximum of ${maxBrokerAccounts} broker account${maxBrokerAccounts === 1 ? "" : "s"} allowed.`
          : platformPlan === "growth"
            ? "Growth (Forex) allows only 1 GenesisFX account."
            : platformPlan === "general" && genesisConnectionCount >= 1
              ? "General plan allows only 1 GenesisFX account (and 1 Webull)."
              : "Your plan cannot connect GenesisFX.",
      );
      return;
    }
    if (atBrokerLimit) {
      showToast(
        "error",
        `Maximum of ${maxBrokerAccounts} broker accounts allowed.`,
      );
      return;
    }
    if (!genesisFxEmail.trim()) {
      showToast("error", "Please enter your email");
      return;
    }
    if (!genesisFxPassword.trim()) {
      showToast("error", "Please enter your password");
      return;
    }
    try {
      setIsConnecting(true);
      showToast("info", "Connecting to GenesisFX...");
      const result = await brokerageApi.UserLoginGenesis(
        genesisFxEmail.trim(),
        genesisFxPassword,
      );

      if (result.success && result.data?.accounts?.length) {
        showToast(
          "success",
          result.message || "Credentials verified. Select an account.",
        );
        setGenesisFxAccounts(result.data.accounts);
        setGenesisFxConnectionId(result.data.connection?.id || null);
        setSelectedGenesisAccountId(result.data.accounts[0]?.id || null);
        setGenesisFxStep("select-account");
      } else {
        showToast("error", result.message || "Failed to connect to GenesisFX");
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      const rawMessage = errorData?.message ?? errorData?.error;
      const errorMessage =
        typeof rawMessage === "string"
          ? rawMessage
          : typeof rawMessage === "object" && rawMessage?.message
            ? String(rawMessage.message)
            : error.message ||
            "Invalid email or password. Please check your GenesisFX credentials.";
      showToast("error", errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnectAlpaca = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!alpacaApiKey.trim()) {
      showToast("error", "Please enter your API key");
      return;
    }
    if (!alpacaApiSecret.trim()) {
      showToast("error", "Please enter your API secret");
      return;
    }
    connectAlpacaMutation.mutate({
      apiKey: alpacaApiKey.trim(),
      apiSecret: alpacaApiSecret.trim(),
      isPaper: alpacaIsPaper,
    });
  };

  const handleSelectGenesisAccount = async () => {
    if (!genesisFxConnectionId) {
      showToast("error", "Connection session expired. Please sign in again.");
      setGenesisFxStep("credentials");
      return;
    }

    const selectedAccount = genesisFxAccounts.find(
      (account) => account.id === selectedGenesisAccountId,
    );
    if (!selectedAccount) {
      showToast("error", "Please select an account");
      return;
    }

    try {
      setIsConnecting(true);
      const result = await brokerageApi.selectGenesisAccount(
        genesisFxConnectionId,
        selectedAccount.id,
        selectedAccount.accountNumber,
      );

      if (result.success) {
        showToast(
          "success",
          result.message || "GenesisFX account connected successfully!",
        );
        resetGenesisFxModal();
        await finalizeGenesisConnection(genesisFxConnectionId);
      } else {
        showToast("error", result.message || "Failed to save selected account");
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      const rawMessage = errorData?.message ?? errorData?.error;
      const errorMessage =
        typeof rawMessage === "string"
          ? rawMessage
          : typeof rawMessage === "object" && rawMessage?.message
            ? String(rawMessage.message)
            : error.message || "Failed to save selected account";
      showToast("error", errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  const getBrokerageLogo = (
    brokerageId: string,
    brokerageName?: string,
    connectionType?: string,
  ) => {
    const id = brokerageId?.toLowerCase() || "";
    const name = brokerageName?.toLowerCase() || "";
    const type = connectionType?.toLowerCase() || "";

    // Check for OANDA
    if (id === "oanda" || name.includes("oanda") || type === "oanda") {
      return (
        <img
          src="/oanda.png"
          alt="OANDA"
          className="w-10 h-10 object-contain"
        />
      );
    }
    // Check for GenesisFX
    if (id.includes('genesis') || name.includes('genesis') || type === 'genesis') {
      return (
        <img
          src="/brokerages/genesisfx.png"
          alt="GenesisFX"
          className="w-10 h-10 object-contain rounded-lg"
        />
      );
    }
    // Check for Alpaca (direct integration)
    if (id === "alpaca" || name.includes("alpaca") || type === "alpaca") {
      return (
        <img
          src="/alpaca.png"
          alt="Alpaca"
          className="w-10 h-10 object-contain"
        />
      );
    }
    // Check for Webull
    if (id.includes("webull") || name.includes("webull")) {
      return (
        <img
          src="/webull.png"
          alt="Webull"
          className="w-10 h-10 object-contain"
        />
      );
    }

    const emojiMap: Record<string, string> = {
      interactive_brokers: "🏦",
      questrade: "📊",
      wealthsimple: "💚",
      td_ameritrade: "🟢",
      etrade: "⭐",
      tastytrade: "🎯",
      fidelity: "💎",
      trading212: "📈",
      freetrade: "🇬🇧",
    };
    return <span className="text-3xl">{emojiMap[id] || "🏦"}</span>;
  };

  if (loadingConnections) {
    return (
      <Loader fullScreen={false} message="Loading brokerage connections..." />
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiGlobe className="lg:h-8 h-6 lg:w-8 w-6 text-primary" />
          <h2 className="lg:text-3xl text-lg font-bold text-foreground">
            Brokerage Accounts
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Sync button - always visible */}
          <button
            onClick={handleManualSync}
            disabled={isConnecting}
            className="btn-secondary flex items-center gap-2"
            title="Sync accounts from SnapTrade"
          >
            <FiRefreshCw
              className={`h-4 w-4 ${isConnecting ? "animate-spin" : ""}`}
            />
            <span>{isConnecting ? "Syncing..." : "Sync"}</span>
          </button>
          {atBrokerLimit && (
            <span className="text-sm text-warning flex items-center gap-1">
              <FiXCircle className="h-4 w-4" />
              Max {maxBrokerAccounts} accounts
            </span>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div
        style={{ display: connections.length > 0 ? "none" : "block" }}
        className="card-premium relative border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-blue-100/10 p-0 shadow-lg overflow-hidden"
      >
        <div className="absolute -top-10 -right-16 w-52 h-52 rounded-full bg-blue-400/10 blur-2xl z-0" />
        <div className="absolute -bottom-10 -left-16 w-48 h-48 rounded-full bg-green-400/10 blur-2xl z-0" />
        <div className="relative flex flex-col md:flex-row items-center gap-4 md:gap-8 p-4 md:p-6">
          {/* Icons Container - Both icons in one line */}
          <div className="flex items-center gap-4 md:gap-8">
            {/* Webull Premium Card */}
            <div className="flex flex-col items-center gap-2 w-auto md:w-32">
              <div className="w-16 h-16 rounded-xl bg-[#a5d4f0] shadow-lg flex items-center justify-center mb-2 border-2">
                <img
                  src="/webull.png"
                  alt="Webull"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <span className="px-3 py-1 text-xs font-semibold bg-blue-500/20 text-blue-400 rounded-lg shadow-sm">
                Webull
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 md:gap-8">
            <div className="flex flex-col items-center gap-2 w-auto md:w-32">
              <div className="w-16 h-16 rounded-xl shadow-lg flex items-center justify-center mb-2 border-2">
                <img
                  src="/genesisfx.png"
                  alt="GenesisFX"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <span className="px-3 py-1 text-xs font-semibold bg-green-500/20 text-green-400 rounded-lg shadow-sm">
                GenesisFX
              </span>
            </div>
          </div>
          {/* Text Block */}
          <div className="flex-1 md:ml-8 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 drop-shadow-sm">
              Supported Brokerages
            </h3>
            <p className="text-muted text-sm md:text-base mb-3">
              Connect{" "}
              <span className="font-semibold text-blue-600">Webull</span>,{" "}
              {/* <span className="font-semibold text-amber-600">Alpaca</span>, and{" "} */}
              <span className="font-semibold text-purple-600">GenesisFX</span>. You
              can connect multiple brokers at once.
            </p>
            <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
              <span className="px-2 py-1 text-xs bg-blue-500/10 text-blue-400 font-medium rounded">
                WEBULL
              </span>
              {/* <span className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 font-medium rounded">
                ALPACA
              </span> */}
              <span className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 font-medium rounded">
                GENESISFX
              </span>
              {/* <span className="px-2 py-1 text-xs bg-indigo-500/10 text-indigo-400 font-medium rounded">CAPITAL.COM</span> */}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Expired Warning */}
      {hasBrokerageConnected && !isMaster && !hasActiveSubscription && (
        <div className="card-premium border-warning/30 bg-warning/5 mb-6">
          <div className="flex items-start gap-4">
            <div className="bg-warning/20 p-3 rounded-lg">
              <FiLock className="h-6 w-6 text-warning" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Subscription Expired
              </h3>
              <p className="text-muted lg:text-lg text-[14px] mb-4">
                Your subscription has expired. Your brokerage accounts will be
                disconnected, and all active copies will be closed. Please renew
                your subscription to continue. To renew or purchase a
                subscription, please contact the administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connect Brokerage Cards */}
      <div style={{ display: connections.length > 0 ? "none" : "block" }}>
        {brokerPlanHint && !isMaster && (
          <p className="mb-4 text-sm text-white/50">{brokerPlanHint}</p>
        )}
        {!atBrokerLimit && (isMaster || hasActiveSubscription) && (
          <div className="grid grid-cols-1 gap-6 mb-6">
            {/* Webull Card */}
            {canConnectWebull && (
            <div
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 cursor-pointer ${(!isMaster && !hasActiveSubscription) || isConnecting
                ? "border-warning/30 bg-warning/5 opacity-60 cursor-not-allowed"
                : "border-green-500/30 bg-gradient-to-br from-green-500/10 via-emerald-600/5 to-teal-500/10 hover:border-green-500/50 hover:shadow-lg hover:shadow-green-500/20"
                }`}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-1">
                      <img src="/webull.png" className="w-14 h-14" /> Connect
                      Webull
                    </h3>
                    <p className="text-xs text-muted"></p>
                  </div>
                </div>
                <p className="text-sm text-muted mb-4">
                  Connect your Webull or Webull Canada. Connect your brokerage
                  account securely.
                </p>
                <button
                  onClick={handleConnectSnapTrade}
                  disabled={
                    isConnecting || (!isMaster && !hasActiveSubscription)
                  }
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${!isMaster && !hasActiveSubscription
                    ? "bg-warning/20 text-warning cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600 group-hover:scale-101"
                    }`}
                >
                  {isConnecting
                    ? "Connecting..."
                    : !isMaster && !hasActiveSubscription
                      ? "Subscribe to Connect"
                      : "Connect Brokerage"}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            </div>
            )}

            {/* Alpaca Card - Direct API integration */}
            {/* <div
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                (!isMaster && !hasActiveSubscription) || isConnecting
                  ? "border-warning/30 bg-warning/5 opacity-60 cursor-not-allowed"
                  : "border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-yellow-600/5 to-orange-500/10 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/20 cursor-pointer"
              }`}
              onClick={() => {
                if (isMaster || hasActiveSubscription) {
                  setShowAlpacaModal(true);
                }
              }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <img
                        src="/alpaca.png"
                        alt="Alpaca"
                        className="w-12 h-12 object-contain rounded-lg"
                      />
                      Connect Alpaca
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-muted mb-4">
                  Connect your Alpaca account directly with API keys. Supports paper
                  and live trading.
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMaster || hasActiveSubscription) {
                      setShowAlpacaModal(true);
                    }
                  }}
                  disabled={
                    isConnecting || (!isMaster && !hasActiveSubscription)
                  }
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${
                    !isMaster && !hasActiveSubscription
                      ? "bg-warning/20 text-warning cursor-not-allowed"
                      : "bg-amber-500 text-white hover:bg-amber-600 group-hover:scale-101"
                  }`}
                >
                  {isConnecting
                    ? "Connecting..."
                    : !isMaster && !hasActiveSubscription
                      ? "Subscribe to Connect"
                      : "Connect Alpaca"}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            </div> */}

            {/* Forex Brokerage Card - OANDA */}

            {/* <div
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                (!isMaster && !hasActiveSubscription) || isConnecting
                  ? "border-warning/30 bg-warning/5 opacity-60 cursor-not-allowed"
                  : "border-blue-500/30 bg-gradient-to-br from-blue-500/10 via-indigo-600/5 to-purple-500/10 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/20 cursor-pointer"
              }`}
              onClick={() => {
                if (isMaster || hasActiveSubscription) {
                  setShowOandaModal(true);
                }
              }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <img src="./oanda.png" className="w-12 h-10" /> Connect OANDA
                    </h3>
                    <p className="text-xs text-muted"></p>
                  </div>
                </div>
                <p className="text-sm text-muted mb-4">
                  Connect your OANDA account. Connect your brokerage account
                  securely.
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMaster || hasActiveSubscription) {
                      setShowOandaModal(true);
                    }
                  }}
                  disabled={
                    isConnecting || (!isMaster && !hasActiveSubscription)
                  }
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${
                    !isMaster && !hasActiveSubscription
                      ? "bg-warning/20 text-warning cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600 group-hover:scale-101"
                  }`}
                >
                  {isConnecting
                    ? "Connecting..."
                    : !isMaster && !hasActiveSubscription
                      ? "Subscribe to Connect"
                      : "Connect Forex Brokerage"}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            </div> */}

            {canConnectGenesis && (
            <div
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${(!isMaster && !hasActiveSubscription) || isConnecting
                ? "border-warning/30 bg-warning/5 opacity-60 cursor-not-allowed"
                : "border-purple-500/30 bg-gradient-to-br from-purple-500/10 via-violet-600/5 to-fuchsia-500/10 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 cursor-pointer"
                }`}
              onClick={() => {
                if (isMaster || hasActiveSubscription) {
                  setShowGenesisFxModal(true);
                }
              }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <img
                        src="/brokerages/genesisfx.png"
                        alt="GenesisFX"
                        className="w-12 h-12 object-contain rounded-lg"
                      />
                      Connect GenesisFX
                    </h3>
                    <p className="text-xs text-muted"></p>
                  </div>
                </div>
                <p className="text-sm text-muted mb-4">
                  Connect your GenesisFX account using your email and password.
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMaster || hasActiveSubscription) {
                      setShowGenesisFxModal(true);
                    }
                  }}
                  disabled={
                    isConnecting || (!isMaster && !hasActiveSubscription)
                  }
                  className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${!isMaster && !hasActiveSubscription
                    ? "bg-warning/20 text-warning cursor-not-allowed"
                    : "bg-purple-500 text-white hover:bg-purple-600 group-hover:scale-101"
                    }`}
                >
                  {isConnecting
                    ? "Connecting..."
                    : !isMaster && !hasActiveSubscription
                      ? "Subscribe to Connect"
                      : "Connect GenesisFX"}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Active Brokers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Active Brokers
          </h3>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-medium px-2 py-1 rounded ${atBrokerLimit
                ? "bg-warning/20 text-warning"
                : "bg-muted/20 text-muted"
                }`}
            >
              {connections.length}/{maxBrokerAccounts} accounts
            </span>
          </div>
        </div>
        <div className="grid gap-4">
          {connections.map((connection) => (
            <div
              key={connection._id}
              className={`card-premium ${connection.isPrimary ? "border-primary/50" : ""
                } ${!hasActiveSubscription && !isMaster ? "border-warning/30 bg-warning/5" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getBrokerageLogo(
                    connection.brokerageId,
                    connection.brokerageName,
                    connection.connectionType,
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-semibold text-foreground">
                        {connection.brokerageName}
                      </h4>
                      {connection.isPrimary && (
                        <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-full flex items-center gap-1">
                          <FiStar className="w-3 h-3" /> Primary
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full ${connection.accountType === "paper"
                          ? "bg-blue-500/20 text-blue-400"
                          : "bg-green-500/20 text-green-400"
                          }`}
                      >
                        {connection.accountType === "paper"
                          ? "📄 Demo"
                          : "💰 Live"}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full ${connection.status === "active"
                          ? "bg-success/20 text-success"
                          : connection.status === "error"
                            ? "bg-danger/20 text-danger"
                            : "bg-warning/20 text-warning"
                          }`}
                      >
                        {connection.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted">
                      <span>
                        Balance:{" "}
                        <span className="text-foreground font-medium">
                          ${connection.accountBalance?.toLocaleString() || "0"}
                        </span>{" "}
                        {connection.currency}
                      </span>
                      <span>
                        Buying Power:{" "}
                        <span className="text-foreground font-medium">
                          ${connection.buyingPower?.toLocaleString() || "0"}
                        </span>
                      </span>
                    </div>
                    {connection.lastSynced && (
                      <p className="text-xs text-muted mt-1">
                        Last synced:{" "}
                        {new Date(connection.lastSynced).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSync(connection._id)}
                    disabled={syncingConnection === connection._id}
                    className="btn-secondary p-2 disabled:opacity-50"
                    title="Sync Account"
                  >
                    <FiRefreshCw
                      className={`w-4 h-4 ${syncingConnection === connection._id ? "animate-spin" : ""}`}
                    />
                  </button>
                  {!connection.isPrimary && connections.length > 1 && false && (
                    <button
                      onClick={() => handleSetPrimary(connection._id)}
                      disabled={settingPrimaryId === connection._id}
                      className="btn-secondary p-2 disabled:opacity-50"
                      title="Set as Primary"
                    >
                      {settingPrimaryId === connection._id ? (
                        <FiRefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <FiStar className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openDisconnectConfirm(connection)}
                    disabled={disconnectingId === connection._id}
                    className="btn-danger p-2 disabled:opacity-50"
                    title="Disconnect"
                  >
                    {disconnectingId === connection._id ? (
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <FiTrash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {connections.length > 0 && !atBrokerLimit && (
        <div className="flex flex-wrap justify-end gap-2 w-full">
          {/* {canConnectGenesis && ( */}
          <button
            className="bg-[#9333EA] text-white px-5 py-2 rounded-md hover:opacity-90 transition"
            onClick={() => setShowGenesisFxModal(true)}
          >
            Connect GenesisFX
          </button>
          {/* )}
          {canConnectWebull && ( */}
          <button
            className="bg-[#22C55E] text-white px-5 py-2 rounded-md hover:opacity-90 transition"
            onClick={handleConnectSnapTrade}
          >
            Connect Webull
          </button>
          {/* )} */}
        </div>
      )}

      {/* Account Details - Show for display connection */}
      {showAccountDetails && displayConnection && (
        <div>
          {/* Use connection object's cached values */}
          {/* This ensures correct data is shown when a specific broker is selected */}
          {loadingPrimaryData ? (
            <Loader fullScreen={false} message="Loading account details..." />
          ) : primaryConnectionData?.account ? (
            <AccountDetails
              account={primaryConnectionData.account}
              positions={primaryConnectionData.positions || []}
              connectionId={displayConnection._id}
              connectionType={displayConnection.connectionType}
              brokerageLogo={
                displayConnection.connectionType === "genesis"
                  ? "/brokerages/genesisfx.png"
                  : displayConnection.connectionType === "alpaca"
                    ? "/alpaca.png"
                    : undefined
              }
              onRefresh={async () => {
                setLoadingPrimaryData(true);
                try {
                  await loadConnections();
                  const updatedConnections =
                    await brokerageApi.getConnections();
                  const updatedDisplay = selectedBrokerId
                    ? updatedConnections.connections?.find(
                      (c) => c._id === selectedBrokerId,
                    )
                    : updatedConnections.connections?.find(
                      (c) => c.isPrimary,
                    ) || displayConnection;
                  if (!updatedDisplay) {
                    console.error("No connection found to refresh");
                    return;
                  }
                  const liveAccountRes = await brokerageApi.getAccount(
                    updatedDisplay._id,
                  );
                  const liveAccount = liveAccountRes?.account || {};
                  let positions: any[] = [];
                  try {
                    const positionsRes = await brokerageApi.getPositions(
                      updatedDisplay._id,
                    );
                    if (positionsRes.success) {
                      positions = positionsRes.positions || [];
                    }
                  } catch (posError) {
                    console.error("Failed to fetch positions:", posError);
                  }

                  setPrimaryConnectionData({
                    account: {
                      id:
                        liveAccount.id ||
                        updatedDisplay.accountId ||
                        updatedDisplay._id.toString(),
                      portfolio_value:
                        liveAccount.portfolio_value ?? liveAccount.equity ?? 0,
                      cash: liveAccount.cash ?? 0,
                      equity:
                        liveAccount.equity ?? liveAccount.portfolio_value ?? 0,
                      buying_power:
                        liveAccount.buying_power ??
                        liveAccount.buyingPower ??
                        0,
                      currency:
                        liveAccount.currency ||
                        updatedDisplay.currency ||
                        "USD",
                      accountType:
                        liveAccount.accountType ||
                        updatedDisplay.accountType ||
                        "live",
                    },
                    positions: positions,
                    brokerageName: updatedDisplay.brokerageName,
                  });
                } catch (error) {
                  console.error("Failed to refresh:", error);
                } finally {
                  setLoadingPrimaryData(false);
                }
              }}
              isRefreshing={loadingPrimaryData}
              brokerageName={
                primaryConnectionData.brokerageName ||
                displayConnection.brokerageName
              }
            />
          ) : (
            <div className="card-premium border-warning/30 bg-warning/5">
              <div className="flex items-center gap-2 text-warning">
                <FiXCircle className="h-5 w-5" />
                <span>
                  Account details not available for{" "}
                  {displayConnection.brokerageName}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {!hasBrokerageConnected && (
        <>
          {/* Subscription Required Message */}
          {!isMaster && !hasActiveSubscription && (
            <div className="card-premium border-warning/30 bg-warning/5">
              <div className="flex items-start gap-4">
                <div className="bg-warning/20 p-3 rounded-lg">
                  <FiLock className="h-6 w-6 text-warning" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Subscription Required
                  </h3>
                  <p className="text-muted mb-4">
                    Subscribe first, then you can connect your brokerage
                    account.
                  </p>
                  <button
                    onClick={handleSubscribe}
                    disabled={createCheckoutMutation.isPending}
                    className="btn-primary flex items-center gap-2"
                  >
                    <FiDollarSign className="h-5 w-5" />
                    <span>
                      {createCheckoutMutation.isPending
                        ? "Processing..."
                        : "Buy Subscription ($599)"}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Simple Add Broker Button */}
          <div className="card-premium">
            <div className="text-center py-8">
              <FiLink className="h-16 w-16 mx-auto text-primary mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No Brokerage Connected
              </h3>
              <p className="text-muted text-sm">
                Use the connection cards above to connect your brokerage
                account.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Disconnect brokerage confirmation */}
      {disconnectPending && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-background rounded-lg shadow-xl max-w-md w-full border border-border"
            role="dialog"
            aria-modal="true"
            aria-labelledby="disconnect-broker-title"
          >
            <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-3">
              <h3
                id="disconnect-broker-title"
                className="text-lg font-semibold text-foreground"
              >
                Disconnect brokerage?
              </h3>
              <button
                type="button"
                onClick={() => !disconnectingId && setDisconnectPending(null)}
                disabled={!!disconnectingId}
                className="text-muted hover:text-foreground transition-colors disabled:opacity-50 p-1 rounded-lg"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted leading-relaxed">
                Are you sure you want to disconnect{" "}
                <span className="font-semibold text-foreground">
                  {disconnectPending.brokerName}
                </span>
                ? This will remove the link from Trade Pilot and stop copy
                trading that uses this account.
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-secondary px-4 py-2"
                  onClick={() => setDisconnectPending(null)}
                  disabled={!!disconnectingId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-danger px-4 py-2 inline-flex items-center gap-2"
                  disabled={!!disconnectingId}
                  onClick={async () => {
                    const ok = await performDisconnect(
                      disconnectPending.connectionId,
                    );
                    if (ok) setDisconnectPending(null);
                  }}
                >
                  {disconnectingId === disconnectPending.connectionId ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Disconnecting…
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="w-4 h-4" />
                      Disconnect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Broker Change Confirmation Modal */}
      {pendingPrimaryChange && (
        <PrimaryBrokerChangeModal
          isOpen={showPrimaryChangeModal}
          onClose={() => {
            setShowPrimaryChangeModal(false);
            setPendingPrimaryChange(null);
          }}
          onConfirm={handleConfirmPrimaryChange}
          activeRelationships={pendingPrimaryChange.activeRelationships}
          newBrokerName={pendingPrimaryChange.connectionName}
        />
      )}

      {/* GenesisFX Connection Modal */}
      {showGenesisFxModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <img src="/brokerages/genesisfx.png" alt="GenesisFX" className="w-8 h-8 object-contain rounded" />
                Connect GenesisFX
              </h3>
              <button
                onClick={resetGenesisFxModal}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <p className="text-sm text-foreground">
                  {genesisFxStep === "credentials"
                    ? "Enter your GenesisFX credentials to connect your brokerage account securely."
                    : "Select the GenesisFX account you want to connect to Trade Polit."}
                </p>
              </div>

              {genesisFxStep === "credentials" ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleConnectGenesis();
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="genesisfx-server"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Server
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiServer className="h-5 w-5 text-muted" />
                      </div>
                      <input
                        id="genesisfx-server"
                        type="text"
                        value={GENESIS_FX_SERVER}
                        readOnly
                        className="input-field pl-10 bg-muted/20 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="genesisfx-email"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="h-5 w-5 text-muted" />
                      </div>
                      <input
                        id="genesisfx-email"
                        type="email"
                        placeholder="Enter your GenesisFX email"
                        value={genesisFxEmail}
                        onChange={(e) => setGenesisFxEmail(e.target.value)}
                        className="input-field pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="genesisfx-password"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiKey className="h-5 w-5 text-muted" />
                      </div>
                      <input
                        id="genesisfx-password"
                        type="password"
                        placeholder="Enter your GenesisFX password"
                        value={genesisFxPassword}
                        onChange={(e) => setGenesisFxPassword(e.target.value)}
                        className="input-field pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={resetGenesisFxModal}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isConnecting}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isConnecting ? (
                        <>
                          <FiRefreshCw className="h-5 w-5 animate-spin" />
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <FiLink className="h-5 w-5" />
                          <span>Connect</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Select Account
                    </label>
                    <div className="space-y-2">
                      {genesisFxAccounts.map((account) => {
                        const isSelected = selectedGenesisAccountId === account.id;
                        const label =
                          account.displayName ||
                          (account.name && !account.name.startsWith("GENFX#")
                            ? account.name
                            : `Account ${account.accountNumber}`);
                        return (
                          <button
                            key={`${account.id}-${account.accountNumber}`}
                            type="button"
                            onClick={() => setSelectedGenesisAccountId(account.id)}
                            className={`w-full text-left p-4 rounded-lg border transition-colors ${isSelected
                              ? "border-purple-500 bg-purple-500/10"
                              : "border-border hover:border-purple-500/50"
                              }`}
                          >
                            <div className="font-medium text-foreground">{label}</div>
                            <div className="text-sm text-muted mt-1">
                              Account ID: {account.accountNumber}
                            </div>
                            {account.balance != null && (
                              <div className="text-sm text-muted">
                                Balance: {account.balance} {account.currency}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setGenesisFxStep("credentials");
                        setGenesisFxAccounts([]);
                        setGenesisFxConnectionId(null);
                        setSelectedGenesisAccountId(null);
                      }}
                      className="btn-secondary flex-1"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectGenesisAccount}
                      disabled={isConnecting || !selectedGenesisAccountId}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isConnecting ? (
                        <>
                          <FiRefreshCw className="h-5 w-5 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FiLink className="h-5 w-5" />
                          <span>Connect Account</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Webull account picker (after SnapTrade OAuth — same pattern as Genesis) */}
      {showWebullSelectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <img src="/webull.png" alt="Webull" className="w-8 h-8 object-contain rounded" />
                Connect Webull
              </h3>
              <button
                onClick={resetWebullSelectModal}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-foreground">
                  Select the Webull account you want to connect to Trade Polit.
                </p>
              </div>

              {loadingWebullAccounts ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <FiRefreshCw className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted">Loading accounts…</p>
                </div>
              ) : webullAccounts.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted text-center py-4">
                    No accounts found yet. If you just authorized Webull, wait a few seconds and try again.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={resetWebullSelectModal}
                      className="btn-secondary flex-1"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={openWebullAccountPicker}
                      disabled={isConnecting}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <FiRefreshCw className={`h-4 w-4 ${isConnecting ? "animate-spin" : ""}`} />
                      Retry
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Select Account
                    </label>
                    <div className="space-y-2">
                      {webullAccounts.map((account) => {
                        const isSelected = selectedWebullAccountId === account.id;
                        const label =
                          account.displayName ||
                          account.name ||
                          account.brokerageName ||
                          `Account ${account.accountNumber || account.id.slice(0, 8)}`;
                        return (
                          <button
                            key={account.id}
                            type="button"
                            disabled={!!account.alreadyConnected}
                            onClick={() => {
                              if (!account.alreadyConnected) {
                                setSelectedWebullAccountId(account.id);
                              }
                            }}
                            className={`w-full text-left p-4 rounded-lg border transition-colors ${
                              account.alreadyConnected
                                ? "border-border bg-muted/20 opacity-60 cursor-not-allowed"
                                : isSelected
                                  ? "border-blue-500 bg-blue-500/10"
                                  : "border-border hover:border-blue-500/50"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium text-foreground">{label}</div>
                              {account.alreadyConnected && (
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-success">
                                  Connected
                                </span>
                              )}
                            </div>
                            {account.accountNumber && (
                              <div className="text-sm text-muted mt-1">
                                Account #: {account.accountNumber}
                              </div>
                            )}
                            {account.brokerageName && (
                              <div className="text-sm text-muted">
                                Broker: {account.brokerageName}
                              </div>
                            )}
                            {account.balance != null && (
                              <div className="text-sm text-muted">
                                Balance: {account.balance} {account.currency}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={resetWebullSelectModal}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectWebullAccount}
                      disabled={
                        isConnecting ||
                        !selectedWebullAccountId ||
                        !!webullAccounts.find((a) => a.id === selectedWebullAccountId)
                          ?.alreadyConnected
                      }
                      className="btn-primary flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isConnecting ? (
                        <>
                          <FiRefreshCw className="h-5 w-5 animate-spin" />
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <FiLink className="h-5 w-5" />
                          <span>Connect Account</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alpaca Connection Modal */}
      {showAlpacaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <img src="/alpaca.png" alt="Alpaca" className="w-8 h-8 object-contain" />
                Connect Alpaca
              </h3>
              <button
                onClick={resetAlpacaModal}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div
                className={`p-4 rounded-lg border ${alpacaIsPaper
                  ? "bg-amber-500/10 border-amber-500/30"
                  : "bg-warning/10 border-warning/30"
                  }`}
              >
                <p className="text-sm text-foreground">
                  Connect directly to Alpaca using your API keys. Keys are sent
                  securely to the server and never stored in your browser.
                </p>
              </div>

              <div
                className={`p-4 rounded-lg border ${alpacaIsPaper
                  ? "border-primary/30 bg-primary/5"
                  : "border-warning/30 bg-warning/5"
                  }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  {alpacaIsPaper ? (
                    <FiShield className="h-5 w-5 text-primary" />
                  ) : (
                    <FiDollarSign className="h-5 w-5 text-warning" />
                  )}
                  <h4 className="font-semibold text-foreground">
                    How to get your {alpacaIsPaper ? "Paper" : "Live"} API keys
                  </h4>
                </div>
                <ol className="space-y-2 text-sm text-foreground">
                  <li>
                    1. Go to{" "}
                    <a
                      href="https://alpaca.markets"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary font-semibold inline-flex items-center gap-1"
                    >
                      alpaca.markets <FiExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                  <li>
                    2. Open{" "}
                    <strong>
                      {alpacaIsPaper ? "Paper Trading → API Keys" : "Live Trading → API Keys"}
                    </strong>
                  </li>
                  <li>3. Generate new keys with <strong>Trading</strong> permission</li>
                  <li>4. Paste your keys below</li>
                </ol>
              </div>

              <form onSubmit={handleConnectAlpaca} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Account Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setAlpacaIsPaper(true)}
                      className={`p-3 rounded-lg border-2 transition-all ${alpacaIsPaper
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted hover:border-primary/50"
                        }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <FiShield className="h-5 w-5" />
                        <span className="font-semibold text-sm">Paper</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAlpacaIsPaper(false)}
                      className={`p-3 rounded-lg border-2 transition-all ${!alpacaIsPaper
                        ? "border-warning bg-warning/10 text-warning"
                        : "border-border bg-background text-muted hover:border-warning/50"
                        }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <FiDollarSign className="h-5 w-5" />
                        <span className="font-semibold text-sm">Live</span>
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="alpaca-api-key"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    API Key
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiKey className="h-5 w-5 text-muted" />
                    </div>
                    <input
                      id="alpaca-api-key"
                      type="text"
                      value={alpacaApiKey}
                      onChange={(e) => setAlpacaApiKey(e.target.value)}
                      placeholder="Enter your Alpaca API key"
                      className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="alpaca-api-secret"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    API Secret
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiKey className="h-5 w-5 text-muted" />
                    </div>
                    <input
                      id="alpaca-api-secret"
                      type="password"
                      value={alpacaApiSecret}
                      onChange={(e) => setAlpacaApiSecret(e.target.value)}
                      placeholder="Enter your Alpaca API secret"
                      className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {!alpacaIsPaper && (
                  <div className="p-3 rounded-lg bg-warning/20 border border-warning text-sm text-foreground">
                    Live trading uses real money. Make sure you are using Live
                    Trading API keys, not Paper keys.
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetAlpacaModal}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={connectAlpacaMutation.isPending}
                    className="btn-primary flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {connectAlpacaMutation.isPending ? (
                      <>
                        <FiRefreshCw className="h-5 w-5 animate-spin" />
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <FiLink className="h-5 w-5" />
                        <span>Connect Alpaca</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* OANDA Connection Modal */}
      {/* {showOandaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground">
                Connect OANDA Forex Brokerage
              </h3>
              <button
                onClick={() => {
                  setShowOandaModal(false);
                  setOandaAccessToken("");
                }}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-foreground">
                  Enter your OANDA access token to connect your forex brokerage
                  account securely.
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!oandaAccessToken.trim()) {
                    showToast("error", "Please enter your access token");
                    return;
                  }
                  saveOandaTokenMutation.mutate(oandaAccessToken);
                }}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="oanda-access-token"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    OANDA Access Token
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiKey className="h-5 w-5 text-muted" />
                    </div>
                    <input
                      id="oanda-access-token"
                      type="password"
                      placeholder="Enter your OANDA access token"
                      value={oandaAccessToken}
                      onChange={(e) => setOandaAccessToken(e.target.value)}
                      className="input-field pl-10"
                      required
                    />
                  </div>
                </div>

                {saveOandaTokenMutation.isError && (
                  <div className="p-4 rounded-lg bg-danger/10 text-danger border border-danger/30">
                    <div className="flex items-start gap-2">
                      <FiXCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold mb-1">
                          Failed to Save Token
                        </p>
                        <p className="text-sm">
                          {(() => {
                            const error: any = saveOandaTokenMutation.error;
                            return (
                              error?.response?.data?.error ||
                              error?.message ||
                              "Failed to save access token. Please try again."
                            );
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOandaModal(false);
                      setOandaAccessToken("");
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    disabled={saveOandaTokenMutation.isPending}
                  >
                    {saveOandaTokenMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FiLink className="h-5 w-5" />
                        <span>Save Token</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ConnectBrokerage;
